/**
 * useSubscriptionStatus Hook (Web)
 * Thin wrapper around shared useSubscriptionStatus, adds web-specific portal redirect
 */

import { useAuth } from '@contexts/AuthContext';
import { createHttpClient } from '@parlay-picker/shared/api/httpClient';
import { useSubscriptionStatus as useSubscriptionStatusShared } from '@parlay-picker/shared/hooks';
import { useCallback, useMemo } from 'react';
import { API_BASE_URL } from '../config/api';

// Re-export types for existing consumers
export type { SubscriptionInfo } from '@parlay-picker/shared/hooks';

export interface UseSubscriptionStatusResult {
  subscription: import('@parlay-picker/shared/hooks').SubscriptionInfo | null;
  loading: boolean;
  error: string | null;
  openBillingPortal: () => Promise<void>;
  portalLoading: boolean;
  refetch: () => Promise<void>;
}

export function useSubscriptionStatus(): UseSubscriptionStatusResult {
  const { authFetch } = useAuth();

  const httpClient = useMemo(
    () => createHttpClient({ authFetch, baseUrl: API_BASE_URL }),
    [authFetch],
  );

  const shared = useSubscriptionStatusShared({ httpClient });

  // Web-specific: navigate via window.location
  const openBillingPortal = useCallback(async () => {
    const url = await shared.getBillingPortalUrl();
    if (url) {
      window.location.href = url;
    }
  }, [shared]);

  return {
    subscription: shared.subscription,
    loading: shared.loading,
    error: shared.error,
    openBillingPortal,
    portalLoading: shared.portalLoading,
    refetch: shared.refetch,
  };
}
